import React, { useMemo } from 'react';
import './style.scss';
import InformationContainer from './containers/InformationContainer';
import JUNOAttendButton from '../../components/JunoAttendButton';
import JUNOAddToCalendarButton from '../../components/JUNOAddToCalendarButton';
import { FontAwesomeConfigurations } from '../../../utils/Configuration/FontAwesomeConfiguration';
import DOMPurify from 'dompurify';

interface MyScheduleRowModuleProps {
  content: any;
  hasPreReqs: boolean;
  contentDeleted: (row, content) => void;
  onAttendButtonChange: () => void;
  onRefresh: () => void;
  onViewPost: (content) => void;
  parentPanel: any;
}

interface MyScheduleRowState {
  numAttending: number;
  dateTime: any;
}

const MyScheduleRowModule: React.FC<MyScheduleRowModuleProps> = props => {
  const [moduleState, setModuleState] = React.useState<MyScheduleRowState>({
    numAttending: props.content.NumAttending,
    dateTime: getDateAndTime(props.content),
  });

  const sessionType = props.content.GetFirstTagOfType(CrowdHub_App.TAGTYPE_SESSION)?.Name;
  const attendButton = getAttendButton(props.content, props.onAttendButtonChange, setModuleState, moduleState);
  const showCancelButton = moduleState.numAttending <= 0 ? false : true;

  const sanitizedLocation = useMemo(() => {
    const sanitizedString = DOMPurify.sanitize(props.content.Location as string, {});
    // uncomment to debug what's getting removed
    // console.log('Dirty values', DOMPurify.removed);
    return sanitizedString;
  }, [props.content.Location]);

  const extraCriteria: JSX.Element[] = [];
  if (props.content.Exclusive === 'true') {
    const className =
      props.content.Schedulable === 'true' && props.content.hasAccess() == true
        ? 'first_criteria_container fa-stack fa-2x'
        : 'first_criteria_container bumpDown fa-stack fa-2x';
    const exclusiveIcon = (
      <span className={className} key={extraCriteria.length + 1}>
        <i className="first_criteria_background fa-solid fa-square fa-stack-2x"></i>
        <i
          className={`first_criteria_icon ${FontAwesomeConfigurations.ICON_SHIELD_KEYHOLE_SOLID} fa-stack-1x fa-inverse`}
        />
      </span>
    );
    extraCriteria.push(exclusiveIcon);

    if (props.hasPreReqs) {
      const className =
        props.content.Schedulable === 'true' && props.content.hasAccess() == true
          ? 'second_criteria_container fa-stack fa-2x'
          : 'second_criteria_container bumpDown fa-stack fa-2x';
      const prereqIcon = (
        <span className={className} key={extraCriteria.length + 1}>
          <i className="second_criteria_background fa-solid fa-square fa-stack-2x"></i>
          <i
            className={`second_criteria_icon ${FontAwesomeConfigurations.ICON_LIST_TREE_SOLID} fa-stack-1x fa-inverse`}
          />
        </span>
      );
      extraCriteria.push(prereqIcon);
    }
  } else if (props.hasPreReqs) {
    const className =
      props.content.Schedulable === 'true' && props.content.hasAccess() == true
        ? 'first_criteria_container fa-stack fa-2x'
        : 'first_criteria_container bumpDown fa-stack fa-2x';
    const exclusiveIcon = (
      <span className={className}>
        <i className="first_criteria_background fa-solid fa-square fa-stack-2x"></i>
        <i className={`first_criteria_icon ${FontAwesomeConfigurations.ICON_LIST_TREE_SOLID} fa-stack-1x fa-inverse`} />
      </span>
    );
    extraCriteria.push(exclusiveIcon);
  }

  const onCancelMeetingWrapper = e => {
    e.stopPropagation();
    onCancelMeeting(props.content, showCancelButton, moduleState, setModuleState, props.onRefresh);
  };

  const shrinkContainer =
    (!props.content.hasAccess() || props.content.Schedulable === 'false') &&
    !props.hasPreReqs &&
    props.content.Exclusive === 'false';
  const leftContainerClassName = shrinkContainer ? 'left_container shrinkMobile' : 'left_container';
  return (
    <div className="ui_relative myschedule_row">
      {moduleState.dateTime.date && <div className="split_container">{moduleState.dateTime.date}</div>}
      <div className="row_container">
        <div className={leftContainerClassName}>
          <div className="date_information_container">
            {!CrowdHub_App.CONFIG.HIDE_START_DATES && (
              <div className="date_time_container">
                <i className={`clock_icon ${FontAwesomeConfigurations.ICON_CLOCK}`} />
                <div style={{ paddingRight: '10px' }}>{moduleState.dateTime.time}</div>
                {CrowdHub_App.CONFIG.SHOW_ADD_TO_CALENNDAR && props.content.hasAccess() && (
                  <JUNOAddToCalendarButton content={props.content} />
                )}
              </div>
            )}
            {props.content.Location && (
              <div className="location_container">
                <i className={`location_icon ${FontAwesomeConfigurations.ICON_LOCATION_DOT_SOLID}`} />
                <span
                  dangerouslySetInnerHTML={{
                    __html: sanitizedLocation,
                  }}
                ></span>
              </div>
            )}
            {extraCriteria.length > 0 && extraCriteria}
            {CrowdHub_App.CONFIG.SHOW_ATTEND_BUTTON_ON_LIST_VIEW &&
              props.content.GetActionById(CrowdHub_App.ACTION_ATTEND) !== null &&
              attendButton}
          </div>
        </div>
        <div className="right_container">
          <InformationContainer
            content={props.content}
            onViewPost={props.onViewPost}
            numAttending={moduleState.numAttending}
            showCancelLink={showCancelButton}
            onCancelMeeting={onCancelMeetingWrapper}
          />
        </div>
      </div>
    </div>
  );
};

const getDateAndTime = content => {
  let date;
  const start = content.MomentDateStart.local();
  var end = content.MomentDateEnd.local();

  var aMonthDay = start.format('MMMM Do');
  var time = content.getTimeDisplay();

  const aLastDate = start.format('dddd, MMMM Do');
  if (document.documentElement.innerHTML.search(aLastDate) == -1) {
    date = aLastDate;
  }
  return { date, time };
};

const getAttendButton = (content, onAttendButtonChange, setModuleState, moduleState) => {
  if (!content.hasAccess()) {
    return undefined;
  }

  if (content.Schedulable === 'false') {
    return undefined;
  }

  var aAttendAction = content.GetActionById(CrowdHub_App.ACTION_ATTEND);
  var aMaxMembers = content.MetaDataSingleValueForKey(CrowdHub_App.META_MAXUSERS);

  const isInPast = content.currentState() === JunoContent.STATE_PAST;
  const isIrlSession = content.FirstBucketName() === 'IRLSession';

  // if the session is over, I need to either show the past lingo, or allow attend if irl and config is on
  if (isInPast) {
    if (!isIrlSession) {
      //  we are a reg session, show past
      return <div className="attend_label">{CrowdHub_App.LANG.WARNING_PASTEVENT}</div>;
    }
    if (isIrlSession && !CrowdHub_App.CONFIG.ALLOW_IRL_ATTEND_PAST_SESSION) {
      // we are in a irl session, and config is off, show past
      return <div className="attend_label">{CrowdHub_App.LANG.WARNING_PASTEVENT}</div>;
    }
  }

  // if maxmembers is hit and user has not completed, show full event
  if (aMaxMembers != null && aAttendAction.UserCompleted == false) {
    if (parseInt(aMaxMembers) <= parseInt(moduleState.numAttending)) {
      return <div className="attend_label">{CrowdHub_App.LANG.WARNING_FULLEVENT}</div>;
    }
  }

  if (content.hasAccess()) {
    return (
      <JUNOAttendButton
        content={content}
        action={content.GetActionById(CrowdHub_App.ACTION_ATTEND)}
        buttonStyle={'juno_attend_button ' + (CrowdHub_App.CONFIG.HIDE_POINTS ? 'hide_points' : '')}
        text="Attend"
        overrideText={CrowdHub_App.LANG.SESSION_ATTEND_TEXT}
        overrideTextCompleted={CrowdHub_App.LANG.SESSION_ATTENDING_TEXT}
        canUnComplete={true}
        loginReason={CrowdHub_App.LANG.LOGINREASON_ATTEND}
        showLoader={true}
        onComplete={action => {
          if (isIrlSession && isInPast && CrowdHub_App.CONFIG.ALLOW_IRL_ATTEND_PAST_SESSION) {
            let confirmPopup = new ConfirmPopupPanel();
            confirmPopup.showOkConfirm(
              'Past Session',
              'Please be aware that you are adding an expired session to your schedule',
            );
          }
          onAttend(action, content, setModuleState, moduleState);
          if (typeof onAttendButtonChange === 'function') {
            onAttendButtonChange();
          }
        }}
        onUnComplete={action => {
          onUnAttend(action, content, setModuleState, moduleState);
          if (typeof onAttendButtonChange === 'function') {
            onAttendButtonChange();
          }
        }}
      />
    );
  }

  return <></>;
};

const onCancelMeeting = (content, showCancelButton, moduleState, setModuleState, onRefresh) => {
  const aUrl = 'custom_api/api_Services_Opentok.php';

  // We're going to want a popup, so the confirm button is what will fire the API call
  const confirmButton = {
    buttonCallback: () => {
      // hit the cancel meeting API
      const aVars: any = {};
      aVars.api = 'api_cancel_meeting';
      aVars.user_id = App.INSTANCE.CurrentUser.Id;
      aVars.content_id = content.Id;

      App.INSTANCE.mApi.Request(
        CrowdHub_App.CONFIG.BASE_URL + aUrl,
        aVars,
        successResponse => {
          // Replace the cancel link with 'canceled'
          if (showCancelButton) {
            showCancelButton = false;
          }
          const action = content.GetActionById(CrowdHub_App.ACTION_ATTEND);
          action.UnComplete(action.ContentId, () => {
            onUnAttend(action, content, setModuleState, moduleState);
          });
        },
        errorResponse => {
          const fullXHR = errorResponse.fullXHR;
          const serverResponse = errorResponse.serverResponse;
          const errorThrown = errorResponse.errorThrown;
          const errorUserMessage = errorResponse.message;

          console.log(
            'API error: ' +
              errorThrown +
              '\n\tServerResponse: ' +
              serverResponse +
              '\n\tSee Stack for full XHR Response: SchedulePartnerMeetingPanel.prototype.getTimes',
          );
          alert(errorUserMessage + '\n\tPlease try again later');
        },
      );
    },
    buttonId: 'confirm_cancel_meeting_button',
    buttonStyle: 'standard_button_outline',
    buttonText: CrowdHub_App.LANG.B2B_CANCEL_MEETING_CONFIRM,
    showLoader: false,
  };

  // create cancel button
  const cancelButton = {
    buttonCallback: () => {},
    buttonId: 'cancel_cancel_meeting_button',
    buttonStyle: 'standard_button_filled',
    buttonText: CrowdHub_App.LANG.B2B_CANCEL_MEETING_CANCEL,
    showLoader: false,
  };

  var buttonArray = [confirmButton, cancelButton];

  const confirmPopup = new ConfirmPopupPanel();
  confirmPopup.showMultiButtonConfirm(
    CrowdHub_App.LANG.B2B_CANCEL_MEETING_TITLE,
    CrowdHub_App.LANG.B2B_CANCEL_MEETING_SUBTITLE,
    buttonArray,
  );
};

const onAttend = (action, content, setModuleState, moduleState) => {
  App.INSTANCE.reportActivity(content.Id, 'Content', 'Attend', content.Name);
  App.INSTANCE.firehoseActivity(
    ANALYTICS_CONFIGURATION.FIREHOSE_OBJECTS.OBJECT_CONTENT,
    content.Id,
    App.INSTANCE.appRouter.lastRouteResolvedUrl(),
    App.INSTANCE.CurrentUser.Id,
    content.FirstBucketName(),
    ANALYTICS_CONFIGURATION.FIREHOSE_ACTIONS.ACTION_SCHEDULE.value,
    null,
    null,
  );
  App.INSTANCE.getScheduledNotifications();

  setModuleState({ ...moduleState, numAttending: action.TotalCompleted });
};

const onUnAttend = (action, content, setModuleState, moduleState) => {
  App.INSTANCE.firehoseActivity(
    ANALYTICS_CONFIGURATION.FIREHOSE_OBJECTS.OBJECT_CONTENT,
    content.Id,
    App.INSTANCE.appRouter.lastRouteResolvedUrl(),
    App.INSTANCE.CurrentUser.Id,
    content.FirstBucketName(),
    ANALYTICS_CONFIGURATION.FIREHOSE_ACTIONS.ACTION_UNSCHEDULE.value,
    null,
    null,
  );
  App.INSTANCE.reportActivity(content.Id, 'Content', 'Attend', content.Name);
  App.INSTANCE.getScheduledNotifications();

  setModuleState({ ...moduleState, numAttending: action.TotalCompleted });
};

export default MyScheduleRowModule;
